// The one place `import.meta.env` is read, validated once at module-eval
// time. Same arrangement as apps/retells/src/env.ts and for the same reason:
// `import.meta.env` is fixed for the life of a Vite build, so one fail-fast
// parse turns a runtime `undefined` deep in some module into a message a
// developer sees immediately.
//
// Two fields, one required: `VITE_CONVEX_URL` is a `z.url()` with no
// default, so this parse is exactly what refuses to boot a bundle built
// with it unset or malformed (env.schema.ts's header says why that is the
// right failure). The other value of the arrangement is that both names
// have exactly one reader and one schema, so `bun run env:example` and
// `bun run env:check` describe what the code actually does.
import type * as z from "zod";
import { envSchema } from "./env.schema";

/**
 * `import.meta.env`, with empty strings read as absent.
 *
 * `X=` in a `.env.local` is somebody deferring the decision, not making one —
 * the rule `@bindery/shared/env`'s `envReader` and `env-check.ts` both apply,
 * so a Vite build must not be the one surface that disagrees. Here it is
 * load-bearing rather than cosmetic: `VITE_FACSIMILE_BASE=` would otherwise
 * parse as `""` and defeat its own default, making every facsimile URL a
 * bare relative key.
 */
function source(): Record<string, unknown> {
  const raw = import.meta.env as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const name of Object.keys(envSchema.shape)) {
    const value = raw[name];
    if (value !== undefined && value !== "") out[name] = value;
  }
  return out;
}

export const env: z.infer<typeof envSchema> = envSchema.parse(source());
